import type { RepwiseEvent } from '@repwise/domain';
import type { SyncStatus } from '@repwise/sync';
import { allEntries, type Entry } from './store.ts';
import { flush } from './log.ts';

export interface DiagSummary {
  entries: number;
  hangs: number;
  longestHangMs: number;
  errors: number;
  slow: number;
  unexpectedEnds: number;
  maxLagMs: number;
  from?: number;
  to?: number;
}

export function summarise(entries: readonly Entry[]): DiagSummary {
  const ends = entries.filter((e) => e.area === 'hang' && e.event === 'end');
  const starts = entries.filter((e) => e.area === 'hang' && e.event === 'start');
  return {
    entries: entries.length,
    hangs: Math.max(starts.length, ends.length),
    longestHangMs: Math.max(0, ...ends.map((e) => Number(e.data?.ms ?? 0))),
    errors: entries.filter((e) => e.level === 'error').length,
    slow: entries.filter((e) => e.area === 'perf' && e.level === 'warn').length,
    unexpectedEnds: entries.filter((e) => e.event === 'previous_run_ended_unexpectedly').length,
    maxLagMs: Math.max(0, ...entries.filter((e) => e.event === 'event_loop_lag').map((e) => Number(e.data?.ms ?? 0))),
    ...(entries.length ? { from: entries[0]!.t, to: entries.at(-1)!.t } : {}),
  };
}

/** The whole report: device and app context, counts only from the event log (never its contents), and the log. */
export async function buildReport(o: { build: string; events: readonly RepwiseEvent[] | null; status: SyncStatus | null }) {
  await flush();
  const entries = await allEntries();
  const byType: Record<string, number> = {};
  for (const e of o.events ?? []) byType[e.type] = (byType[e.type] ?? 0) + 1;
  const nav = navigator as Navigator & { deviceMemory?: number };
  const est = await nav.storage?.estimate?.().catch(() => undefined);
  return {
    format: 'repwise-diagnostics',
    version: 1,
    createdAt: new Date().toISOString(),
    app: { build: o.build },
    device: {
      userAgent: nav.userAgent.slice(0, 300),
      standalone: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
      screen: `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio}`,
      cores: nav.hardwareConcurrency ?? null,
      memoryGb: nav.deviceMemory ?? null,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: nav.language,
      online: nav.onLine,
    },
    storage: est ? { usageMb: Math.round((est.usage ?? 0) / 1e4) / 100, quotaMb: Math.round((est.quota ?? 0) / 1e4) / 100 } : null,
    eventLog: { total: o.events?.length ?? 0, byType },
    sync: o.status ? { label: o.status.label, state: o.status.state, pending: o.status.pending, deadLetters: o.status.deadLetters, lastSyncAt: o.status.lastSyncAt ?? null, retry: o.status.retry ?? null } : null,
    summary: summarise(entries),
    entries,
  };
}

export type Report = Awaited<ReturnType<typeof buildReport>>;

/** Fits the feedback table's 16 KB context: the summary, the hangs and errors, and the latest entries. */
export function compact(r: Report, maxBytes = 14_000): Record<string, unknown> {
  const important = r.entries.filter((e) => e.area === 'hang' || e.level === 'error' || e.event === 'previous_run_ended_unexpectedly').slice(-30);
  let tail = 80;
  for (;;) {
    const out = { format: r.format, version: r.version, createdAt: r.createdAt, app: r.app, device: r.device, storage: r.storage, eventLog: { total: r.eventLog.total }, sync: r.sync, summary: r.summary, important, latest: r.entries.slice(-tail) };
    if (JSON.stringify(out).length <= maxBytes || tail === 0) return out;
    tail = Math.max(0, tail - 20);
    if (tail === 0) important.splice(0, Math.max(0, important.length - 10));
  }
}
